import { Camera as BJSCamera } from "@babylonjs/core/Cameras/camera";
import { Ray } from "@babylonjs/core/Culling/ray";
import { Frustum } from "@babylonjs/core/Maths/math.frustum";
import { Plane } from "@babylonjs/core/Maths/math.plane";
import { Quaternion, Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { ListenableEvent } from "../../utils";
import { Timer } from "../../utils/async";
import { clamp, EPSILON, eq, PI_DIV2, PI_MUL2 } from "../../utils/math";
import { deserializeVector3, serializeVector3 } from "../io/Serialization";
import { BoundingBox, NodeUserData } from "../mdl";
import { InteractionMode } from "../tool/Tool";
import { intersectRayPlane, normalizeAngle, projectPointOnPlane, rotateVector3ToRef, VECTOR3_X, VECTOR3_Y, VECTOR3_Z } from "../utils/MathUtils";
import { Camera } from "./Camera";
import { PointerButton, PointerButtonFlags } from "./InputState";
import { CoordinatesType } from "./View";
export const CameraMode = {
    //fixed direction modes
    Top: 0,
    Bottom: 1,
    Left: 2,
    Right: 3,
    Front: 4,
    Back: 5,
    //free direction modes
    Orthographic: 6,
    Perspective: 7
};
export const CameraModeMask = {
    None: 0x00000000,
    Top: 0x00000001,
    Bottom: 0x00000002,
    Left: 0x00000004,
    Right: 0x00000008,
    Front: 0x00000010,
    Back: 0x00000020,
    Orthographic: 0x00000040,
    Perspective: 0x00000080,
    All: 0xFFFFFFFF
};
export class CameraControl {
    get view() {
        return (this.mView);
    }
    get cameraMode() {
        return (this.mCameraMode);
    }
    set cameraMode(pMode) {
        this.setCameraMode(pMode);
    }
    get position() {
        return (this.mCamera.position);
    }
    get direction() {
        return (this.mDirection);
    }
    get right() {
        return (this.mRight);
    }
    get viewPlane() {
        return (this.mViewPlane);
    }
    get fixedTarget() {
        return (this.mFixedTarget);
    }
    get transformMatrix() {
        if (this.mTransformMatrix == null) {
            this.mTransformMatrix = this.mCamera.getViewMatrix().multiply(this.mCamera.getProjectionMatrix());
        }
        return (this.mTransformMatrix);
    }
    get orbitInertia() {
        return (this.mOrbitInertia);
    }
    set orbitInertia(pValue) {
        this.mOrbitInertia = clamp(pValue, 0, 1);
    }
    get navigationArea() {
        return ({ radius: this.mNavAreaRadius, center: this.mNavAreaCenter });
    }
    constructor(pViewer, pView) {
        this.eventCameraModeChanged = new ListenableEvent(this);
        this.eventCameraChanged = new ListenableEvent(this);
        this.onOrbitStopped = () => {
            this.mOrbitStarted = false;
        };
        this.mViewer = pViewer;
        this.mView = pView;
        this.mCamera = pView.camera;
        this.mModel = this.mViewer.app.model;
        this.mToolManager = this.mViewer.app.tools;
        this.orbitSpeed = 0.005;
        this.wheelZoomSpeed = 0.0015;
        this.touchZoomSpeed = 1;
        this.mOrbitRawRotation = Vector3.Zero();
        this.mOrbitInertia = 0;
        this.mOrbitDelta = Vector2.Zero();
        this.mOrbitStartTime = Number.MAX_VALUE;
        this.mOrbitStarted = false;
        this.mOrbitStoppedTimer = new Timer(this.onOrbitStopped);
        this.mApplyOrbitInertia = false;
        this.pitchMax = PI_DIV2 - EPSILON;
        this.pitchMin = -this.pitchMax;
        this.yawMax = PI_MUL2;
        this.yawMin = -PI_MUL2;
        this.setNavigationArea(20);
        this.controlsEnabled = true;
        this.orbitEnabled = true;
        this.panningEnabled = true;
        this.zoomEnabled = true;
        this.orthoOrbitSnappingEnabled = false;
        this.dblClickZoomToFitEnabled = true;
        this.dblClickZoomToFitOptions = { margin: 0.05 };
        this.leftMouseButtonEnabled = true;
        this.mCameraMode = CameraMode.Perspective;
        this.mCameraModeMask = CameraModeMask.Perspective;
        this.mIsDirectionLocked = false;
        this.mWasCameraChanged = true;
        this.mChanged = false;
        this.mChangeDepth = 0;
        this.mTransformMatrix = null;
        this.mPanningPickPos = null;
        this.mOrbitPickPos = null;
        this.mZoomPickPos = null;
        this.mZoomPickDistance = 0;
        this.mToolInteractionMode = InteractionMode.None;
        this.mGroundPlane = new Plane(0, 1, 0, 0);
        this.mTargetDistance = this.mNavAreaRadius;
        this.mDirection = VECTOR3_Z.clone();
        this.mRight = VECTOR3_X.clone();
        this.mCameraModeSettings = new Map();
        this.mCamera.minZ = 0.1;
        this.mCamera.maxZ = 100;
        this.mCamera.notifyProjectionChanged();
        this.resetCamera(true, false);
        this.updateDirection();
        this._updateCSSPerspective();
        this.mFixedTarget = null;
    }
    /**
     * Copies the camera settings (position, direction, target, mode ...) from the given CameraControl to this.
     */
    copyCameraSettingsFrom(pOther) {
        if (pOther == null)
            return;
        this.beginChange();
        this.mCamera.minZ = pOther.mCamera.minZ;
        this.mCamera.maxZ = pOther.mCamera.maxZ;
        this.cameraMode = pOther.cameraMode;
        this.setFov(pOther.mCamera.fov);
        this.setFixedTarget(pOther.fixedTarget);
        this.setDirection(pOther.direction);
        this.setPosition(pOther.position);
        this.setCameraOrtho(pOther.mCamera.orthoTop);
        this.endChange();
    }
    setPosition(pPosition, pKeepFixedTarget = true) {
        if (pPosition == null)
            return;
        this.mCamera.position = pPosition.clone();
        if (this.mFixedTarget != null) {
            if (pKeepFixedTarget) {
                this.mCamera.setTarget(this.mFixedTarget);
                this.updateDirection();
                this.updateTargetDistance(null);
            }
            else {
                this.mFixedTarget = null;
            }
        }
        this.updateViewPlane();
        this.updateTargetPlane();
        this.notifyChange();
    }
    setDirection(pDirection, pUpVector) {
        if (this.mFixedTarget == null) {
            return (this.setTarget(this.mCamera.position.add(pDirection), pUpVector));
        }
        else {
            if (this.mIsDirectionLocked || pDirection == null)
                return (false);
            const tDistance = this.mFixedTarget.subtract(this.mCamera.position).length();
            const tPos = this.mFixedTarget.add(pDirection.scale(-tDistance));
            this.setPosition(tPos);
            return (true);
        }
    }
    setTarget(pTarget, pUpVector) {
        if (this.mIsDirectionLocked || pTarget == null || this.mFixedTarget != null)
            return (false);
        this.mCamera.setTarget(pTarget);
        if (pUpVector != null)
            this.mCamera.upVector = pUpVector.clone();
        this.updateDirection();
        this.notifyChange();
        return (true);
    }
    setFixedTarget(pFixedTarget) {
        if (this.mIsDirectionLocked)
            return (false);
        if (pFixedTarget != null) {
            this.mCamera.setTarget(pFixedTarget);
            this.mFixedTarget = pFixedTarget.clone();
            this.updateDirection();
            this.updateTargetDistance(null);
        }
        else
            this.mFixedTarget = null;
        this.notifyChange();
        return (true);
    }
    setCameraOrtho(pOrthoTop) {
        const tOrtho = clamp(pOrthoTop, CameraControl.MIN_ORTHO, this.mMaxZoomOrtho);
        const tAspect = this.mCamera.orthoRight / this.mCamera.orthoTop;
        this.mCamera.orthoTop = tOrtho;
        this.mCamera.orthoBottom = -tOrtho;
        this.mCamera.orthoLeft = -tOrtho * tAspect;
        this.mCamera.orthoRight = tOrtho * tAspect;
        this.mCamera.notifyProjectionChanged();
    }
    setFov(pFov) {
        if (this.mCamera.fov !== pFov) {
            this.mCamera.fov = pFov;
            this.mCamera.notifyProjectionChanged();
            this._updateCSSPerspective();
        }
    }
    /**
     * The navigation area is used to limit certain camera interactions.
     * E.g. the maximum camera distance (zoom) depends on it.
     * The area will not be updated automatically, it should be defined by the host application.
     */
    setNavigationArea(pRadius, pCenter = Vector3.Zero()) {
        this.mNavAreaRadius = pRadius;
        this.mNavAreaCenter = pCenter;
        this.mMaxZoomOrtho = this.mNavAreaRadius;
        this.mMaxZoomDistance = this.mNavAreaRadius / Math.tan(this.mCamera.fov * 0.5);
    }
    getFrustum() {
        return (Frustum.GetPlanes(this.transformMatrix));
    }
    notifyChange(pTriggerChangeEvent = true) {
        this.mChanged = true;
        this.mWasCameraChanged = true;
        this.mTransformMatrix = null;
        if (this.mChangeDepth === 0) {
            this.mChanged = false;
            if (pTriggerChangeEvent)
                this.eventCameraChanged.trigger();
        }
    }
    beginChange() {
        this.mChangeDepth++;
    }
    endChange() {
        this.mChangeDepth--;
        if (this.mChangeDepth <= 0) {
            this.mChangeDepth = 0;
            if (this.mChanged)
                this.notifyChange();
        }
    }
    /**
     * Performs zoom to fit on SceneElement basis.
     * If pElements is null all elements in the scene will be used.
     */
    zoomToFitElements(pElements, pOptions) {
        const tElements = pElements == null ? this.mModel.elements : pElements;
        const tNodes = new Array();
        for (const tElement of tElements) {
            if (tElement.node != null)
                tNodes.push(tElement.node);
        }
        if (this.mCameraMode === CameraMode.Perspective)
            this.zoomToFitPerspective(tNodes, true, pOptions);
        else
            this.zoomToFitOrtho(tNodes, true, pOptions);
    }
    zoomToFitMeshes(pMeshes, pOptions) {
        this.zoomToFitNodes(pMeshes, pOptions);
    }
    zoomToFitNodes(pNodes, pOptions) {
        if (this.mCameraMode === CameraMode.Perspective)
            this.zoomToFitPerspective(pNodes, false, pOptions);
        else
            this.zoomToFitOrtho(pNodes, false, pOptions);
    }
    /**
     * Performs a zoom step targeting the view center with the given amount.
     * Use a positive amount to zoom out and a negative amount to zoom in.
     * The amount typically should be in range [-1.0, 1.0]. 0.15 can be used as a reference value.
     */
    zoomBy(pAmount) {
        //get view center
        const tCenter = new Vector2(this.view.width * 0.5, this.view.height * 0.5);
        //convert to client coordinates
        this.view.convertCoordsToRef(tCenter, CoordinatesType.Viewer, CoordinatesType.Client, tCenter);
        this.wheelZoom(tCenter, pAmount);
    }
    serialize() {
        const tCameraSettings = new Array();
        //stored camera mode settings
        this.mCameraModeSettings.forEach((pSetting, pMode) => {
            if (pMode !== this.cameraMode) {
                const tIOSetting = {};
                tIOSetting.mode = this.cameraModeToString(pMode);
                tIOSetting.position = serializeVector3(pSetting.position);
                tIOSetting.direction = serializeVector3(pSetting.direction);
                tIOSetting.fov = this.mCamera.fov;
                if (pMode != CameraMode.Perspective)
                    tIOSetting.orthoTop = pSetting.orthoTop;
                tCameraSettings.push(tIOSetting);
            }
        });
        //active camera setting
        const tIOSetting = {};
        tIOSetting.active = true;
        tIOSetting.mode = this.cameraModeToString(this.cameraMode);
        tIOSetting.position = serializeVector3(this.position);
        tIOSetting.direction = serializeVector3(this.direction);
        tIOSetting.fov = this.mCamera.fov;
        if (this.cameraMode != CameraMode.Perspective)
            tIOSetting.orthoTop = this.mCamera.orthoTop;
        tCameraSettings.push(tIOSetting);
        return tCameraSettings;
    }
    deserialize(pIOCameraSettings) {
        if (pIOCameraSettings == null)
            return (false);
        const tCameraSettings = new Map();
        for (const tIOSetting of pIOCameraSettings) {
            const tMode = this.cameraModeFromString(tIOSetting.mode);
            const tSetting = {};
            tSetting.position = deserializeVector3(tIOSetting.position);
            tSetting.direction = deserializeVector3(tIOSetting.direction);
            tSetting.orthoTop = tIOSetting.orthoTop != null ? tIOSetting.orthoTop :
                CameraControl.DEFAULT_CAMERA_ORTHO;
            tSetting.fov = tIOSetting.fov != null ? tIOSetting.fov : Camera._DEFAULT_FOV;
            tCameraSettings.set(tMode, tSetting);
            if (tIOSetting.active != null && tIOSetting.active) {
                this.beginChange();
                this.stopOrbitInertia();
                this.cameraMode = tMode;
                this.setFov(tSetting.fov);
                this.setPosition(tSetting.position);
                this.setDirection(tSetting.direction);
                this.setCameraOrtho(tSetting.orthoTop);
                this.endChange();
            }
        }
        this.mCameraModeSettings = tCameraSettings;
        return (true);
    }
    resetCamera(pResetOrthoZoom, pResetCameraModeSettings) {
        this.beginChange();
        this.stopOrbitInertia();
        // set default ortho distance
        if (pResetOrthoZoom)
            this.setCameraOrtho(CameraControl.DEFAULT_CAMERA_ORTHO);
        //reset fixed target
        this.mFixedTarget = null;
        const tHalfFar = this.mCamera.maxZ * 0.5;
        switch (this.mCameraMode) {
            case (CameraMode.Top):
                this.setDirection(new Vector3(0, -1, 0));
                this.setPosition(new Vector3(0, tHalfFar, 0));
                break;
            case (CameraMode.Bottom):
                this.setDirection(new Vector3(0, 1, 0));
                this.setPosition(new Vector3(0, -tHalfFar, 0));
                break;
            case (CameraMode.Left):
                this.setDirection(new Vector3(1, 0, 0));
                this.setPosition(new Vector3(-tHalfFar, 0, 0));
                break;
            case (CameraMode.Right):
                this.setDirection(new Vector3(-1, 0, 0));
                this.setPosition(new Vector3(tHalfFar, 0, 0));
                break;
            case (CameraMode.Front):
                this.setDirection(new Vector3(0, 0, 1));
                this.setPosition(new Vector3(0, 0, -tHalfFar));
                break;
            case (CameraMode.Back):
                this.setDirection(new Vector3(0, 0, -1));
                this.setPosition(new Vector3(0, 0, tHalfFar));
                break;
            case (CameraMode.Perspective):
                this.setPosition(CameraControl.DEFAULT_CAMERA_POSITION);
                this.setTarget(new Vector3(0, 0, 0));
                break;
            case (CameraMode.Orthographic): {
                const tPosDir = CameraControl.DEFAULT_CAMERA_POSITION.normalizeToNew();
                this.setPosition(tPosDir.scaleInPlace(tHalfFar));
                this.setTarget(new Vector3(0, 0, 0));
                break;
            }
        }
        if (pResetCameraModeSettings)
            this.mCameraModeSettings = new Map();
        this.endChange();
    }
    stopOrbitInertia() {
        this.mOrbitStarted = false;
        this.mApplyOrbitInertia = false;
        this.mOrbitStoppedTimer.stop();
    }
    static SetMeshCameraModeMask(pMesh, pMask) {
        NodeUserData.SetData(pMesh, CameraControl.NODEDATA_CAMERAMODEMASK, pMask);
    }
    static GetMeshCameraModeMask(pMesh) {
        return (NodeUserData.GetData(pMesh, CameraControl.NODEDATA_CAMERAMODEMASK));
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    get _camerModeMask() {
        return (this.mCameraModeMask);
    }
    /** @internal */
    _beforeRender() {
        if (this.mApplyOrbitInertia)
            this.updateOrbit();
        const tChanged = this.mWasCameraChanged;
        this.mWasCameraChanged = false;
        return (tChanged);
    }
    /** @internal */
    _updateCSSPerspective() {
        if (this.mView.overlayDiv != null) {
            if (this.mCameraMode === CameraMode.Perspective) {
                const tFovPx = (0.5 / Math.tan(this.mCamera.fov * 0.5) * this.mView.height);
                this.mView.overlayDiv.style.perspective = tFovPx + 'px';
            }
            else {
                this.mView.overlayDiv.style.perspective = "none";
            }
        }
    }
    /** @internal */
    _handlePointerDown(pEvent, pState) {
        this.resetPickState(true);
    }
    /** @internal */
    _handlePointerUp(pEvent, pState) {
        this.resetPickState(false);
    }
    /** @internal */
    _handlePointerDblClick(pEvent, pState) {
        if (!this.controlsEnabled || this.mToolInteractionMode === InteractionMode.All
            || !this.zoomEnabled || !this.dblClickZoomToFitEnabled) {
            return;
        }
        this.zoomToFitElements(null, this.dblClickZoomToFitOptions);
    }
    /** @internal */
    _handlePointerCancel(pEvent, pState) {
        this.resetPickState(true);
    }
    /** @internal */
    _handlePointerMove(pEvent, pState) {
        this.updateToolInteractionMode();
        if (!this.controlsEnabled || this.mToolInteractionMode === InteractionMode.All)
            return;
        if (pState.pointers.has(pEvent.pointerId)) {
            switch (pState.pointers.size) {
                //orbit or right mouse panning
                case 1: {
                    const tPrimaryControlsEnabled = this.mToolInteractionMode === InteractionMode.None;
                    const tPointer = pState.pointers.get(pEvent.pointerId);
                    if (tPointer == null)
                        return;
                    if (tPointer.buttons !== tPointer.previousButtons)
                        this.resetPickState(true);
                    //abort if LMB is disabled
                    if (!this.leftMouseButtonEnabled && tPointer.isMousePointer && tPointer.initialButton === PointerButton.Left)
                        return;
                    const tDoPanning = ((this.mIsDirectionLocked && tPrimaryControlsEnabled) ||
                        tPointer.initialButton === PointerButton.Right ||
                        (tPointer.buttons & PointerButtonFlags.Right) !== 0 ||
                        (pEvent.ctrlKey && tPrimaryControlsEnabled));
                    if (tDoPanning) {
                        this.panning(tPointer.position, tPointer.previousPosition);
                    }
                    else {
                        if (!this.mIsDirectionLocked && (tPrimaryControlsEnabled || (tPointer.buttons & PointerButtonFlags.Middle) !== 0))
                            this.orbit(tPointer.position, tPointer.previousPosition);
                    }
                    break;
                }
                //2-finger zoom and panning
                case 2: {
                    const tPointer1 = pState.getPointerByIndex(0);
                    const tPointer2 = pState.getPointerByIndex(1);
                    if (tPointer1 != null && tPointer2 != null)
                        this.touchZoomPan(tPointer1.position, tPointer2.position, tPointer1.previousPosition, tPointer2.previousPosition);
                    break;
                }
            }
        }
        else {
            this.mZoomPickPos = null;
        }
    }
    /** @internal */
    _handleMouseWheel(pEvent, pState) {
        this.updateToolInteractionMode();
        if (!this.controlsEnabled || this.mToolInteractionMode === InteractionMode.All)
            return;
        let tDelta = pEvent.deltaY;
        if (pEvent.deltaMode === 0) { //pixel mode
            tDelta = clamp(tDelta, -100, 100); //clamp, some browser convert page mode to pixels
        }
        else { //line or page mode
            tDelta = tDelta > 0 ? 100 : -100;
        }
        this.wheelZoom(new Vector2(pEvent.clientX, pEvent.clientY), tDelta * this.wheelZoomSpeed);
    }
    /** @internal */
    static _GetMaskForCameraMode(pMode) {
        switch (pMode) {
            case CameraMode.Top:
                return (CameraModeMask.Top);
            case CameraMode.Bottom:
                return (CameraModeMask.Bottom);
            case CameraMode.Left:
                return (CameraModeMask.Left);
            case CameraMode.Right:
                return (CameraModeMask.Right);
            case CameraMode.Front:
                return (CameraModeMask.Front);
            case CameraMode.Back:
                return (CameraModeMask.Back);
            case CameraMode.Orthographic:
                return (CameraModeMask.Orthographic);
            default:
                return (CameraModeMask.Perspective);
        }
    }
    cameraModeToString(pMode) {
        switch (pMode) {
            case (CameraMode.Top):
                return ("Top");
            case (CameraMode.Bottom):
                return ("Bottom");
            case (CameraMode.Left):
                return ("Left");
            case (CameraMode.Right):
                return ("Right");
            case (CameraMode.Front):
                return ("Front");
            case (CameraMode.Back):
                return ("Back");
            case (CameraMode.Orthographic):
                return ("Orthographic");
            case (CameraMode.Perspective):
                return ("Perspective");
        }
    }
    cameraModeFromString(pMode) {
        switch (pMode) {
            case ("Top"):
                return (CameraMode.Top);
            case ("Bottom"):
                return (CameraMode.Bottom);
            case ("Left"):
                return (CameraMode.Left);
            case ("Right"):
                return (CameraMode.Right);
            case ("Front"):
                return (CameraMode.Front);
            case ("Back"):
                return (CameraMode.Back);
            case ("Orthographic"):
                return (CameraMode.Orthographic);
            default:
                return (CameraMode.Perspective);
        }
    }
    setCameraMode(pMode) {
        if (pMode === this.mCameraMode)
            return;
        const tOldCamMode = this.mCamera.mode;
        if (pMode === CameraMode.Perspective)
            this.mCamera.mode = BJSCamera.PERSPECTIVE_CAMERA;
        else
            this.mCamera.mode = BJSCamera.ORTHOGRAPHIC_CAMERA;
        if (tOldCamMode != this.mCamera.mode)
            this.mCamera.notifyProjectionChanged();
        //stop orbit inertia
        this.stopOrbitInertia();
        //store current settings
        this.mCameraModeSettings.set(this.mCameraMode, {
            position: this.position.clone(),
            direction: this.direction.clone(),
            orthoTop: this.mCamera.orthoTop,
            fov: this.mCamera.fov
        });
        this.mCameraMode = pMode;
        this.mCameraModeMask = CameraControl._GetMaskForCameraMode(this.mCameraMode);
        this.mIsDirectionLocked = false;
        const tDirectionLock = pMode !== CameraMode.Perspective &&
            pMode !== CameraMode.Orthographic;
        if (tDirectionLock)
            this.mFixedTarget = null;
        //restore saved settings
        if (this.mCameraModeSettings.has(pMode)) {
            const tCameraInfo = this.mCameraModeSettings.get(pMode);
            this.setPosition(tCameraInfo.position);
            this.setDirection(tCameraInfo.direction);
            this.setCameraOrtho(tCameraInfo.orthoTop);
        }
        else {
            this.resetCamera(false, false);
        }
        this.mIsDirectionLocked = tDirectionLock;
        this._updateCSSPerspective();
        this.notifyChange();
        this.eventCameraModeChanged.trigger();
    }
    updateDirection() {
        Quaternion.RotationYawPitchRollToRef(this.mCamera.rotation.y, this.mCamera.rotation.x, this.mCamera.rotation.z, gTmpQuaternion1);
        rotateVector3ToRef(VECTOR3_Z, gTmpQuaternion1, this.mDirection);
        rotateVector3ToRef(VECTOR3_X, gTmpQuaternion1, this.mRight);
        this.updateViewPlane();
        this.updateTargetPlane();
    }
    updateViewPlane() {
        this.mViewPlane = Plane.FromPositionAndNormal(this.mCamera.position, this.mDirection);
    }
    updateTargetPlane() {
        const tPlanePosition = this.mCamera.position.add(this.mDirection.scale(this.mTargetDistance));
        this.mTargetPlane = Plane.FromPositionAndNormal(tPlanePosition, this.mDirection.negate());
    }
    getNearestElementCenter() {
        let tCenter = null;
        let tDistanceScore = Number.MAX_VALUE;
        const tFrustum = this.getFrustum();
        for (const tElement of this.mModel.elements) {
            //get bounds in world coordinates
            const tBounds = tElement.boundingBoxVisible.transform(tElement.transform);
            if (tBounds.isInFrustum(tFrustum)) {
                const tElementCenter = tBounds.getCenter();
                const tDistance = Vector3.Distance(tElementCenter, this.mCamera.position);
                let tScore = tDistance;
                //camera inside bounds?
                if (tDistance < tBounds.getRadius()) {
                    const tSize = tBounds.getSize();
                    const tAverageSize = (tSize.x + tSize.y + tSize.z) / 3.0;
                    //prefer small objects (prefer furnitures over rooms/walls)
                    tScore += tAverageSize * 0.5;
                }
                //use element with lowest score
                if (tScore < tDistanceScore || tCenter == null) {
                    tDistanceScore = tScore;
                    tCenter = tElementCenter;
                }
            }
        }
        return (tCenter);
    }
    //returns true in case of direct mesh hit
    updateTargetDistance(pPos, pNoHitMinDist = null) {
        if (pPos == null && this.mFixedTarget == null)
            return (false);
        let tDirectHit = false;
        let tPickPos = null;
        let tMinDist = this.mCamera.minZ * 2;
        let tMaxDist;
        if (this.mCameraMode === CameraMode.Perspective) {
            const tCenterDist = this.mViewPlane.signedDistanceTo(this.mNavAreaCenter);
            tMaxDist = tCenterDist + this.mNavAreaRadius;
        }
        else {
            //orthographic cameras are always positioned far away from the center, we need to use a special fallback here
            tMaxDist = this.mCamera.maxZ * 2 + this.mNavAreaRadius * 2;
        }
        if (this.mFixedTarget != null) {
            tPickPos = this.mFixedTarget;
        }
        else {
            //pick meshes first
            const tPickInfo = this.mView.pickScene(pPos, CoordinatesType.Client);
            if (tPickInfo != null && tPickInfo.hit) {
                tPickPos = tPickInfo.pickedPoint;
                tMinDist = 0.0; // no min
                tMaxDist = Number.MAX_VALUE; // no max
                tDirectHit = true;
            }
            //no mesh picked
            if (tPickPos == null) {
                tPickPos = this.getNearestElementCenter();
                if (tPickPos == null) {
                    //pick ground
                    tPickPos = this.getPlanePickPos(pPos, this.mGroundPlane);
                }
                else {
                    if (pNoHitMinDist != null && pNoHitMinDist > tMinDist)
                        tMinDist = pNoHitMinDist;
                    tMaxDist = Number.MAX_VALUE; // no max
                }
            }
        }
        //calculate new distance
        if (tPickPos != null) {
            this.mTargetDistance = Math.abs(this.mViewPlane.signedDistanceTo(tPickPos));
            //limit max view distance
            if (this.mTargetDistance > tMaxDist)
                this.mTargetDistance = tMaxDist;
        }
        else {
            this.mTargetDistance = tMaxDist;
        }
        //limit min view distance
        if (this.mTargetDistance < tMinDist)
            this.mTargetDistance = tMinDist;
        this.updateTargetPlane();
        return (tDirectHit);
    }
    updateToolInteractionMode() {
        const tTool = this.mToolManager.activeTool;
        this.mToolInteractionMode = tTool != null ? tTool.getInteractionMode() : InteractionMode.None;
    }
    getPlanePickPos(pPos, pPlane) {
        if (pPos == null || pPlane == null)
            return (null);
        this.mView.createPickingRayToRef(pPos, CoordinatesType.Client, null, gTmpRay);
        return (intersectRayPlane(gTmpRay, pPlane));
    }
    resetPickState(pResetAll) {
        this.mPanningPickPos = null;
        this.mZoomPickPos = null;
        this.mApplyOrbitInertia = false;
        if (pResetAll || this.mOrbitInertia === 0) {
            this.mOrbitPickPos = null;
        }
        else {
            if (this.mOrbitInertia > 0) {
                const tOrbitTime = Date.now() - this.mOrbitStartTime;
                this.mApplyOrbitInertia = this.mOrbitStarted && tOrbitTime >= 50;
            }
        }
        this.mOrbitStartTime = Number.MAX_VALUE;
        this.mOrbitStarted = false;
    }
    orbit(pPos, pPrevPos) {
        if (!this.orbitEnabled || pPos == null || pPrevPos == null)
            return;
        if (this.mOrbitPickPos == null) {
            //init orbit
            this.updateTargetDistance(pPrevPos);
            this.mOrbitRawRotation.copyFrom(this.mCamera.rotation);
            if (this.mFixedTarget == null)
                this.mOrbitPickPos = this.getPlanePickPos(pPrevPos, this.mTargetPlane);
            else
                this.mOrbitPickPos = this.mFixedTarget;
            if (this.mOrbitPickPos == null)
                return;
        }
        this.mOrbitDelta.y = (pPrevPos.x - pPos.x) * this.orbitSpeed;
        this.mOrbitDelta.x = (pPrevPos.y - pPos.y) * this.orbitSpeed;
        this.updateOrbit();
        //inertia handling
        if (this.mOrbitInertia > 0) {
            if (!this.mOrbitStarted) {
                this.mOrbitStartTime = Date.now();
                this.mOrbitStarted = true;
            }
            this.mOrbitStoppedTimer.start(50, true);
        }
    }
    updateOrbit() {
        if (this.mOrbitPickPos == null)
            return;
        Quaternion.RotationYawPitchRollToRef(this.mCamera.rotation.y, this.mCamera.rotation.x, this.mCamera.rotation.z, gTmpQuaternion1);
        const tInvQuat = gTmpQuaternion1.conjugateInPlace();
        if (this.mApplyOrbitInertia) {
            this.mOrbitDelta.y *= this.mOrbitInertia;
            this.mOrbitDelta.x *= this.mOrbitInertia;
            //finish animation
            if (eq(this.mOrbitDelta.length(), 0.0, 0.001)) {
                this.mOrbitPickPos = null;
                this.mApplyOrbitInertia = false;
                return;
            }
        }
        //rotate camera
        this.mOrbitRawRotation.x -= this.mOrbitDelta.x;
        this.mOrbitRawRotation.x = normalizeAngle(this.mOrbitRawRotation.x, true);
        this.mOrbitRawRotation.x = clamp(this.mOrbitRawRotation.x, this.pitchMin, this.pitchMax);
        this.mOrbitRawRotation.y -= this.mOrbitDelta.y;
        this.mOrbitRawRotation.y = normalizeAngle(this.mOrbitRawRotation.y, true);
        this.mOrbitRawRotation.y = clamp(this.mOrbitRawRotation.y, this.yawMin, this.yawMax);
        //ortho snapping
        let tSnappedX;
        let tSnappedY;
        if (this.orthoOrbitSnappingEnabled && this.mCameraMode === CameraMode.Orthographic &&
            !this.mApplyOrbitInertia) {
            const tAngle = normalizeAngle(this.mOrbitRawRotation.y, false);
            //snap x
            if (eq(this.mOrbitRawRotation.x, 0.0, CameraControl.OTHO_SNAP_TOLERANCE)) {
                tSnappedX = 0; //center
            }
            else if (eq(this.mOrbitRawRotation.x, PI_DIV2, CameraControl.OTHO_SNAP_TOLERANCE)) {
                tSnappedX = PI_DIV2; //top
            }
            else if (eq(this.mOrbitRawRotation.x, -PI_DIV2, CameraControl.OTHO_SNAP_TOLERANCE)) {
                tSnappedX = -PI_DIV2; //bottom
            }
            //snap y
            if (eq(tAngle, 0.0, CameraControl.OTHO_SNAP_TOLERANCE) ||
                eq(tAngle, PI_MUL2, CameraControl.OTHO_SNAP_TOLERANCE)) {
                tSnappedY = 0;
            }
            else if (eq(tAngle, PI_DIV2, CameraControl.OTHO_SNAP_TOLERANCE)) {
                tSnappedY = PI_DIV2;
            }
            else if (eq(tAngle, Math.PI, CameraControl.OTHO_SNAP_TOLERANCE)) {
                tSnappedY = Math.PI;
            }
            else if (eq(tAngle, Math.PI + PI_DIV2, CameraControl.OTHO_SNAP_TOLERANCE)) {
                tSnappedY = Math.PI + PI_DIV2;
            }
        }
        //assign rotation
        if (tSnappedX != null && tSnappedY != null) {
            this.mCamera.rotation.x = tSnappedX;
            this.mCamera.rotation.y = tSnappedY;
        }
        else {
            this.mCamera.rotation.copyFrom(this.mOrbitRawRotation);
        }
        //correct camera position
        Quaternion.RotationYawPitchRollToRef(this.mCamera.rotation.y, this.mCamera.rotation.x, this.mCamera.rotation.z, gTmpQuaternion2);
        const tQuat = gTmpQuaternion2;
        const tDir = this.mCamera.position.subtract(this.mOrbitPickPos);
        rotateVector3ToRef(tDir, tInvQuat, tDir);
        rotateVector3ToRef(tDir, tQuat, tDir);
        this.mCamera.position = this.mOrbitPickPos.add(tDir);
        //correct up vector if snapped
        if (this.mCamera.rotation.x === PI_DIV2 || this.mCamera.rotation.x === -PI_DIV2) {
            if (this.mCamera.rotation.x === PI_DIV2) {
                this.mCamera.upVector.copyFromFloats(0, 0, 1);
            }
            else {
                this.mCamera.upVector.copyFromFloats(0, 0, -1);
            }
            Quaternion.RotationAxisToRef(VECTOR3_Y, this.mCamera.rotation.y, gTmpQuaternion1);
            rotateVector3ToRef(this.mCamera.upVector, gTmpQuaternion1, this.mCamera.upVector);
        }
        else
            this.mCamera.upVector.copyFromFloats(0, 1, 0);
        this.updateDirection();
        this.notifyChange();
    }
    panning(pPos, pPrevPos) {
        if (!this.panningEnabled || pPos == null)
            return;
        if (this.mPanningPickPos == null) {
            this.updateTargetDistance(pPrevPos);
            this.mPanningPickPos = this.getPlanePickPos(pPrevPos, this.mTargetPlane);
            if (this.mPanningPickPos == null)
                return;
        }
        const tPickPos = this.getPlanePickPos(pPos, this.mTargetPlane);
        if (tPickPos != null) {
            this.mCamera.position.addInPlace(this.mPanningPickPos.subtract(tPickPos));
            if (this.mFixedTarget != null)
                this.mFixedTarget.addInPlace(this.mPanningPickPos.subtract(tPickPos));
        }
        this.notifyChange();
    }
    wheelZoom(pPos, pDelta) {
        if (!this.zoomEnabled || pPos == null || pDelta == null)
            return;
        let tResetZoomPickPos = false;
        if (this.mZoomPickPos == null) {
            //if no mesh was hit, we need to update the view distance with every scroll event
            tResetZoomPickPos = !this.updateTargetDistance(pPos, CameraControl.ZOOM_NO_HIT_MIN_TARGETDISTANCE);
            if (this.mFixedTarget == null)
                this.mZoomPickPos = this.getPlanePickPos(pPos, this.mTargetPlane);
            else
                this.mZoomPickPos = this.mFixedTarget;
            if (this.mZoomPickPos == null)
                return;
        }
        //zoom for ortho cameras
        if (this.mCamera.mode === BJSCamera.ORTHOGRAPHIC_CAMERA) {
            const tOrtho = this.mCamera.orthoTop + pDelta * this.mCamera.orthoTop;
            this.setCameraOrtho(tOrtho);
            if (tOrtho >= CameraControl.MIN_ORTHO && tOrtho <= this.mMaxZoomOrtho) {
                const tProjectedPos = projectPointOnPlane(this.mCamera.position, this.mTargetPlane);
                const tDir = tProjectedPos.subtract(this.mZoomPickPos);
                const tDistance = tDir.length();
                tDir.normalize();
                this.mCamera.position.addInPlace(tDir.scaleInPlace(pDelta * tDistance));
            }
        }
        else {
            this.updatePerspectiveZoom(this.mZoomPickPos, pDelta, true);
        }
        if (tResetZoomPickPos)
            this.mZoomPickPos = null;
        this.updateViewPlane();
        this.notifyChange();
    }
    touchZoomPan(pPos1, pPos2, pPrevPos1, pPrevPos2) {
        if (pPos1 == null || pPos2 == null || pPrevPos1 == null || pPrevPos2 == null)
            return;
        if (this.mZoomPickPos == null) {
            this.updateTargetDistance(Vector2.Center(pPrevPos1, pPrevPos2), CameraControl.ZOOM_NO_HIT_MIN_TARGETDISTANCE);
            const tPickPos1 = this.getPlanePickPos(pPrevPos1, this.mTargetPlane);
            const tPickPos2 = this.getPlanePickPos(pPrevPos2, this.mTargetPlane);
            if (tPickPos1 == null || tPickPos2 == null)
                return;
            this.mPanningPickPos = Vector3.Center(tPickPos1, tPickPos2);
            this.mZoomPickDistance = Vector3.Distance(tPickPos1, tPickPos2);
            this.mZoomPickPos = this.mFixedTarget != null ? this.mFixedTarget : this.mPanningPickPos;
        }
        let tPickPos1 = this.getPlanePickPos(pPos1, this.mTargetPlane);
        let tPickPos2 = this.getPlanePickPos(pPos2, this.mTargetPlane);
        if (tPickPos1 == null || tPickPos2 == null)
            return;
        const tPanPickPos = Vector3.Center(tPickPos1, tPickPos2);
        const tZoomPickPos = this.mFixedTarget != null ? this.mFixedTarget : tPanPickPos;
        let tNotifyChange = false;
        //panning
        if (this.panningEnabled && this.mPanningPickPos != null) {
            this.mCamera.position.addInPlace(this.mPanningPickPos.subtract(tPanPickPos));
            if (this.mFixedTarget != null)
                this.mFixedTarget.addInPlace(this.mPanningPickPos.subtract(tPanPickPos));
            tNotifyChange = true;
        }
        //zoom
        if (this.zoomEnabled) {
            let tPickDistance = Vector3.Distance(tPickPos1, tPickPos2);
            const tDiffDist = this.mZoomPickDistance - tPickDistance;
            if (this.mCamera.mode === BJSCamera.ORTHOGRAPHIC_CAMERA) {
                const tOrtho = this.mCamera.orthoTop + tDiffDist * this.touchZoomSpeed;
                this.setCameraOrtho(tOrtho);
            }
            else {
                this.updatePerspectiveZoom(tZoomPickPos, tDiffDist * 2 * this.touchZoomSpeed);
            }
            //update distance after zoom
            tPickPos1 = this.getPlanePickPos(pPos1, this.mTargetPlane);
            tPickPos2 = this.getPlanePickPos(pPos2, this.mTargetPlane);
            if (tPickPos1 == null || tPickPos2 == null)
                return;
            tPickDistance = Vector3.Distance(tPickPos1, tPickPos2);
            this.mZoomPickDistance = tPickDistance;
            tNotifyChange = true;
        }
        if (tNotifyChange) {
            this.updateViewPlane();
            this.notifyChange();
        }
    }
    updatePerspectiveZoom(pPickPos, pDelta, pMultiplyDeltaWithDistance = false) {
        const tMinDistance = this.mCamera.minZ * 2;
        const tDir = this.mCamera.position.subtract(pPickPos);
        let tDistance = tDir.length();
        tDir.normalize();
        let tDelta = pDelta;
        if (pMultiplyDeltaWithDistance)
            tDelta *= tDistance;
        tDistance += tDelta;
        if ((pDelta > 0 || tDistance > tMinDistance) &&
            (pDelta < 0 || tDistance < this.mMaxZoomDistance)) {
            this.mCamera.position = pPickPos.add(tDir.scaleInPlace(tDistance));
        }
    }
    calculateZoomToFitBounds(pPointCallback, pNode, pProcessChilds) {
        if (pNode == null || !pNode.isEnabled())
            return;
        if (pNode instanceof AbstractMesh) {
            const tIsEmptyMesh = pNode instanceof Mesh && pNode.geometry == null;
            if (pNode.isVisible && !tIsEmptyMesh) {
                const tBInfo = pNode.getBoundingInfo();
                if (tBInfo != null) {
                    for (const tPoint of tBInfo.boundingBox.vectorsWorld) {
                        pPointCallback(tPoint);
                    }
                }
            }
        }
        if (pProcessChilds) {
            const tChilds = pNode.getChildTransformNodes(true);
            for (const tChild of tChilds) {
                this.calculateZoomToFitBounds(pPointCallback, tChild, pProcessChilds);
            }
        }
    }
    zoomToFitOrtho(pNodes, pProcessChildNodes, pOptions) {
        const tOptions = {};
        tOptions.useBoundingSphere = false;
        tOptions.adjustFixedTarget = true;
        if (pOptions != null)
            Object.assign(tOptions, pOptions);
        let tWorldCenter = null;
        let tViewBoundsWidth = 1;
        let tViewBoundsHeight = 1;
        //calculate bounds in view and world space
        const tWorldBounds = new BoundingBox();
        const tViewBounds = tOptions.useBoundingSphere ? null : new BoundingBox();
        const tPointCallback = (pPoint) => {
            tWorldBounds.expandByPointInPlace(pPoint);
            if (tViewBounds != null) {
                const tPoint = this.mView.projectPoint3(pPoint, CoordinatesType.Viewer, null, false);
                if (tPoint != null)
                    tViewBounds.expandByPointInPlace(tPoint);
            }
        };
        for (const tNode of pNodes)
            this.calculateZoomToFitBounds(tPointCallback, tNode, pProcessChildNodes);
        if (tOptions.useBoundingSphere) {
            if (tWorldBounds.isValid()) {
                tWorldCenter = tWorldBounds.getCenter();
                const tWorldRight = tWorldCenter.add(this.mRight.scale(tWorldBounds.getRadius()));
                const tViewCenter = this.mView.projectPoint3(tWorldCenter, CoordinatesType.Viewer, null, false);
                const tViewRight = this.mView.projectPoint3(tWorldRight, CoordinatesType.Viewer, null, false);
                if (tViewCenter != null && tViewRight != null) {
                    const tDistance = Vector3.Distance(tViewCenter, tViewRight);
                    tViewBoundsWidth = tDistance * 2;
                    tViewBoundsHeight = tDistance * 2;
                }
                else {
                    tWorldCenter = null;
                }
            }
        }
        else {
            if (tViewBounds != null && tViewBounds.isValid() && tWorldBounds.isValid()) {
                tWorldCenter = tWorldBounds.getCenter();
                tViewBoundsWidth = (tViewBounds.max.x - tViewBounds.min.x);
                tViewBoundsHeight = (tViewBounds.max.y - tViewBounds.min.y);
            }
        }
        if (tWorldCenter != null) {
            //pan
            const tHalfFar = this.mCamera.maxZ * 0.5;
            this.mCamera.position = tWorldCenter.subtract(this.direction.scale(tHalfFar));
            //zoom
            const tMScaleX = 1.0 - this.getZoomToFitMarginH(tOptions);
            const tMScaleY = 1.0 - this.getZoomToFitMarginV(tOptions);
            const tZoomX = (this.mView.width * tMScaleX) / tViewBoundsWidth;
            const tZoomY = (this.mView.height * tMScaleY) / tViewBoundsHeight;
            const tZoom = Math.min(tZoomX, tZoomY);
            this.setCameraOrtho(this.mCamera.orthoTop / tZoom);
            if (this.mFixedTarget != null && tOptions.adjustFixedTarget)
                this.setFixedTarget(tWorldCenter);
            this.updateViewPlane();
            this.notifyChange();
        }
    }
    zoomToFitPerspective(pNodes, pProcessChildNodes, pOptions) {
        const tOptions = {};
        tOptions.useBoundingSphere = false;
        tOptions.adjustFixedTarget = true;
        if (pOptions != null)
            Object.assign(tOptions, pOptions);
        let tWorldCenter = null;
        const tWorldBounds = new BoundingBox();
        const tPerspectiveBounds = new ZoomToFitPerspectiveBounds(this.mCamera, this.mView, this.getZoomToFitMarginH(tOptions), this.getZoomToFitMarginV(tOptions));
        const tPointCallback = (pPoint) => {
            tWorldBounds.expandByPointInPlace(pPoint);
            if (!tOptions.useBoundingSphere)
                tPerspectiveBounds.expandByPoint(pPoint);
        };
        for (const tNode of pNodes)
            this.calculateZoomToFitBounds(tPointCallback, tNode, pProcessChildNodes);
        if (tWorldBounds.isValid()) {
            tWorldCenter = tWorldBounds.getCenter();
            if (tOptions.useBoundingSphere) {
                const tRadius = tWorldBounds.getRadius() * 0.75; //how to calculate exact value?
                tPerspectiveBounds.expandByPoint(tWorldCenter.add(new Vector3(tRadius, tRadius, tRadius)));
                tPerspectiveBounds.expandByPoint(tWorldCenter.add(new Vector3(-tRadius, tRadius, tRadius)));
                tPerspectiveBounds.expandByPoint(tWorldCenter.add(new Vector3(-tRadius, -tRadius, tRadius)));
                tPerspectiveBounds.expandByPoint(tWorldCenter.add(new Vector3(tRadius, -tRadius, tRadius)));
                tPerspectiveBounds.expandByPoint(tWorldCenter.add(new Vector3(tRadius, tRadius, -tRadius)));
                tPerspectiveBounds.expandByPoint(tWorldCenter.add(new Vector3(-tRadius, tRadius, -tRadius)));
                tPerspectiveBounds.expandByPoint(tWorldCenter.add(new Vector3(-tRadius, -tRadius, -tRadius)));
                tPerspectiveBounds.expandByPoint(tWorldCenter.add(new Vector3(tRadius, -tRadius, -tRadius)));
            }
        }
        if (tWorldCenter != null && tPerspectiveBounds.isValid()) {
            this.mCamera.position = tPerspectiveBounds.getCameraPosition();
            //prevent near clipping of small objects
            const tNearLimit = this.mCamera.minZ * 1.5;
            if (Vector3.Distance(this.mCamera.position, tWorldCenter) < tNearLimit) {
                const tDirOffset = this.mCamera.position.subtract(tWorldCenter).normalize();
                tDirOffset.scaleInPlace(tNearLimit);
                this.mCamera.position.addInPlace(tDirOffset);
            }
            if (this.mFixedTarget != null && tOptions.adjustFixedTarget)
                this.setFixedTarget(tWorldCenter);
            //Workaround to prevent wrong camera position/rotation.
            //see: https://gitlab.easterngraphics.com/wcf-core/core/-/issues/27
            //TODO: try to remove this after BabylonJS update
            this.mCamera.getViewMatrix(true);
            this.updateViewPlane();
            this.notifyChange();
        }
    }
    getZoomToFitMarginV(pOptions) {
        let tMarginV = 0.05; // default
        if (pOptions != null) {
            if (pOptions.margin != null)
                tMarginV = pOptions.margin;
            if (pOptions.minMarginPx != null) {
                if (this.mView.height * tMarginV < pOptions.minMarginPx) {
                    tMarginV = pOptions.minMarginPx / this.mView.height;
                }
            }
        }
        return (tMarginV);
    }
    getZoomToFitMarginH(pOptions) {
        let tMarginH = 0.05; // default
        if (pOptions != null) {
            const tOptionsMargin = pOptions.marginH != null ? pOptions.marginH : pOptions.margin;
            if (tOptionsMargin != null)
                tMarginH = tOptionsMargin;
            const tMinMarginPx = pOptions.minMarginPxH != null ? pOptions.minMarginPxH : pOptions.minMarginPx;
            if (tMinMarginPx != null) {
                if (this.mView.width * tMarginH < tMinMarginPx) {
                    tMarginH = tMinMarginPx / this.mView.width;
                }
            }
        }
        return (tMarginH);
    }
}
/** Default camera ortho/zoom. Used for all camera modes except Perspective. */
CameraControl.DEFAULT_CAMERA_ORTHO = 3.0;
/** Default camera position. Used for camera modes Orthographic and Perspective. */
CameraControl.DEFAULT_CAMERA_POSITION = new Vector3(-1.0, 3.0, -4);
//// private ///////////////////////////////////////////////////////////////////////////////
CameraControl.NODEDATA_CAMERAMODEMASK = "core:cameraModeMask";
CameraControl.ZOOM_NO_HIT_MIN_TARGETDISTANCE = 1;
CameraControl.OTHO_SNAP_TOLERANCE = 0.08;
CameraControl.MIN_ORTHO = 0.01;
//code copied from x-3gpl and modified to left handed coordinate system (z is flipped)
class ZoomToFitPerspectiveBounds {
    constructor(pCamera, pView, pMarginX, pMarginY) {
        this.mMinX = +Number.MAX_VALUE;
        this.mMinXZ = +Number.MAX_VALUE;
        this.mMaxX = -Number.MAX_VALUE;
        this.mMaxXZ = -Number.MAX_VALUE;
        this.mMinY = +Number.MAX_VALUE;
        this.mMinYZ = +Number.MAX_VALUE;
        this.mMaxY = -Number.MAX_VALUE;
        this.mMaxYZ = -Number.MAX_VALUE;
        if (pCamera.fovMode === BJSCamera.FOVMODE_VERTICAL_FIXED) {
            const tAspect = pView.width / pView.height;
            const tFovY = pCamera.fov;
            this.mHalfFovXCos = 1 / (tAspect * Math.tan((1 - pMarginX) * tFovY / 2));
            this.mHalfFovYCos = 1 / Math.tan((1 - pMarginY) * tFovY / 2);
        }
        else {
            const tAspect = pView.height / pView.width;
            const tFovX = pCamera.fov;
            this.mHalfFovXCos = 1 / Math.tan((1 - pMarginX) * tFovX / 2);
            this.mHalfFovYCos = 1 / (tAspect * Math.tan((1 - pMarginY) * tFovX / 2));
        }
        this.mViewMatrix = pCamera.getViewMatrix();
    }
    isValid() {
        return (this.mMinX < this.mMaxX || this.mMinY < this.mMaxY);
    }
    expandByPoint(pPoint) {
        const tPoint = Vector3.TransformCoordinates(pPoint, this.mViewMatrix);
        if (this.mMinX === Number.MAX_VALUE || this.mMinXZ - this.mHalfFovXCos * (tPoint.x - this.mMinX) > tPoint.z) {
            this.mMinX = tPoint.x;
            this.mMinXZ = tPoint.z;
        }
        if (this.mMaxX === -Number.MAX_VALUE || this.mMaxXZ + this.mHalfFovXCos * (tPoint.x - this.mMaxX) > tPoint.z) {
            this.mMaxX = tPoint.x;
            this.mMaxXZ = tPoint.z;
        }
        if (this.mMinY === Number.MAX_VALUE || this.mMinYZ - this.mHalfFovYCos * (tPoint.y - this.mMinY) > tPoint.z) {
            this.mMinY = tPoint.y;
            this.mMinYZ = tPoint.z;
        }
        if (this.mMaxY === -Number.MAX_VALUE || this.mMaxYZ + this.mHalfFovYCos * (tPoint.y - this.mMaxY) > tPoint.z) {
            this.mMaxY = tPoint.y;
            this.mMaxYZ = tPoint.z;
        }
    }
    getCameraPosition() {
        const tX = ((this.mMinXZ - this.mMaxXZ) / this.mHalfFovXCos + this.mMinX + this.mMaxX) / 2;
        const tXZ = this.mMaxXZ + this.mHalfFovXCos * (tX - this.mMaxX);
        const tY = ((this.mMinYZ - this.mMaxYZ) / this.mHalfFovYCos + this.mMinY + this.mMaxY) / 2;
        const tYZ = this.mMaxYZ + this.mHalfFovYCos * (tY - this.mMaxY);
        const tPos = new Vector3(tX, tY, Math.min(tXZ, tYZ));
        Vector3.TransformCoordinatesToRef(tPos, this.mViewMatrix.invert(), tPos);
        return (tPos);
    }
}
//// temp variables ////////////////////////////////////////////////////////////////////////////
const gTmpRay = Ray.Zero();
const gTmpQuaternion1 = Quaternion.Identity();
const gTmpQuaternion2 = Quaternion.Identity();
